import { useState, useEffect } from 'react';
import { Users, Mail, Search, Eye, Trash2, Package, X, User as UserIcon, Phone, MapPin, Heart, Activity, AlertCircle, Plus, EyeOff } from 'lucide-react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function User() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [updatingPackage, setUpdatingPackage] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: '1',
    status: 'approved',
    package: '1',
    created_by: 'superadmin',
    log: 'out'
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPackages();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      // Fetch users with role = 1 (regular users)
      const response = await axios.get(`${BASE_URL}/api/users/role/1/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Users data:', response.data); // Debug log
      setUsers(response.data);
      setFilteredUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      notify.error('Failed to fetch users');
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${BASE_URL}/api/packages/all/`, {  // ✅ Fixed: added /all/
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
      notify.error('Failed to fetch packages');
    }
  };

  const handleOpenPackageModal = (user) => {
    setSelectedUser(user);
    setSelectedPackageId(user.package_ID || '');
    setShowPackageModal(true);
  };

  const handleClosePackageModal = () => {
    setShowPackageModal(false);
    setSelectedUser(null);
    setSelectedPackageId('');
  };

  const handleUpdatePackage = async () => {
    if (!selectedPackageId) {
      notify.error('Please select a package');
      return;
    }

    setUpdatingPackage(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${BASE_URL}/api/users/${selectedUser.user_ID}/update-package/`,
        { package_id: selectedPackageId },  // ✅ Fixed: lowercase package_id
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success('Package updated successfully');
      handleClosePackageModal();
      fetchUsers();
    } catch (error) {
      console.error('Error updating package:', error);
      notify.error(error.response?.data?.error || 'Failed to update package');
    } finally {
      setUpdatingPackage(false);
    }
  };

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = users.filter(user =>
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
    setFilteredUsers(filtered);
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${BASE_URL}/api/user-details/get/${user.user_ID}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserDetails(response.data.user_details);
      } else {
        notify.error(response.data.message || 'Failed to fetch user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      notify.error('Failed to fetch user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setUserDetails(null);
    setSelectedUser(null);
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      notify.error('Please fill in all required fields');
      return;
    }

    setCreatingUser(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${BASE_URL}/api/register/`,
        newUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success || response.status === 201) {
        notify.success('User created successfully');
        setShowCreateModal(false);
        setNewUser({ name: '', email: '', password: '', role: '1', status: 'approved', package: '1', created_by: 'superadmin', log: 'out' });
        fetchUsers();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      notify.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.name || 'N/A',
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-3 py-2">
          {row.profile_pic ? (
            <>
              <img 
                src={row.profile_pic.startsWith('http') ? row.profile_pic : `${BASE_URL}${row.profile_pic}`}
                alt={row.name}
                className="w-8 h-8 rounded-full object-cover border-2 border-primary-500"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.fallback-avatar');
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="fallback-avatar w-8 h-8 bg-primary-500 rounded-full items-center justify-center hidden">
                <span className="text-slate-900 text-xs font-semibold">
                  {row.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            </>
          ) : (
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
              <span className="text-slate-900 text-xs font-semibold">
                {row.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <span className="text-slate-900 text-sm font-medium">{row.name || 'N/A'}</span>
        </div>
      ),
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      cell: row => (
        <div className="flex items-center gap-2 text-slate-700 text-sm">
          <Mail className="w-4 h-4 text-slate-600" />
          {row.email}
        </div>
      ),
    },
    {
      name: 'Package',
      selector: row => row.package_name || 'No Package',
      sortable: true,
      cell: row => (
        <span className="text-slate-700 text-sm">{row.package_name || 'No Package'}</span>
      ),
    },
    {
      name: 'Credits',
      selector: row => row.remaining_credits,
      sortable: true,
      cell: row => (
        <span className="text-slate-700 text-sm">
          {row.remaining_credits !== null && row.remaining_credits !== undefined 
            ? row.remaining_credits 
            : 'Unlimited'}
        </span>
      ),
    },
    {
      name: 'Actions',
      center: true,
      cell: row => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handleOpenPackageModal(row)}
            className="p-2 text-green-600 hover:bg-green-500/10 rounded transition-colors"
            title="Change Package"
          >
            <Package className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleViewDetails(row)}
            className="p-2 text-primary-400 hover:bg-primary-500/10 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.user_ID || row.id)}
            className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        background: 'linear-gradient(to right, #3b82f6, #2563eb)',
        color: 'white',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        minHeight: '48px',
      },
    },
    headCells: {
      style: {
        color: 'white',
        fontSize: '12px',
        fontWeight: '600',
      },
    },
    rows: {
      style: {
        minHeight: '56px',
        '&:hover': {
          backgroundColor: '#f8fafc',
        },
      },
    },
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${BASE_URL}/api/users/${userId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      notify.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      notify.error('Failed to delete user');
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Users Management</h2>
        </div>
        <div className="text-slate-900">
          <span className="text-2xl font-bold">{filteredUsers.length}</span>
          <span className="text-sm text-slate-600 ml-2">Total Users</span>
        </div>
      </div>

      {/* Header with Search and Create Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-medical-500 hover:bg-medical-600 text-white rounded-lg transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Create User
        </button>
        
        <div className="w-80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-3 py-2 bg-primary-50 border-2 border-primary-300 rounded-lg text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm shadow-sm"
            />
          </div>
        </div>
      </div>

      {/* Users DataTable */}
      <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
        <DataTable
          columns={columns}
          data={filteredUsers}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          highlightOnHover
          pointerOnHover
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="text-slate-600 mt-4">Loading users...</p>
            </div>
          }
          noDataComponent={
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-600">No users found</p>
            </div>
          }
        />
      </div>

      {/* Package Update Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Change Package</h3>
              <button
                onClick={handleClosePackageModal}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <div>
                <p className="text-sm text-slate-600 mb-1">User</p>
                <p className="text-base font-medium text-slate-900">{selectedUser?.name}</p>
                <p className="text-sm text-slate-600">{selectedUser?.email}</p>
              </div>

              <div>
                <p className="text-sm text-slate-600 mb-1">Current Package</p>
                <p className="text-base font-medium text-slate-900">
                  {selectedUser?.package_name || 'No Package'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select New Package
                </label>
                <select
                  value={selectedPackageId}
                  onChange={(e) => setSelectedPackageId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                >
                  <option value="">-- Select Package --</option>
                  {packages.map((pkg) => (
                    <option key={pkg.package_ID} value={pkg.package_ID}>
                      {pkg.package_name} - ${pkg.price} ({pkg.credit_limit === 0 ? 'Unlimited' : `${pkg.credit_limit} credits`})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200">
              <button
                onClick={handleClosePackageModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={updatingPackage}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePackage}
                disabled={updatingPackage}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {updatingPackage ? 'Updating...' : 'Update Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-blue-600">
              <h3 className="text-lg font-semibold text-white">User Details</h3>
              <button
                onClick={handleCloseDetailsModal}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <p className="text-slate-600 ml-4">Loading user details...</p>
                </div>
              ) : userDetails ? (
                <div className="space-y-6">
                  {/* Profile Picture */}
                  {selectedUser?.profile_pic && (
                    <div className="flex justify-center">
                      <img 
                        src={selectedUser.profile_pic.startsWith('http') ? selectedUser.profile_pic : `${BASE_URL}${selectedUser.profile_pic}`}
                        alt={userDetails.user_name}
                        className="w-32 h-32 rounded-full object-cover border-4 border-blue-500 shadow-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}

                  {/* Basic Information */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <UserIcon className="w-5 h-5 text-blue-600" />
                      <h4 className="text-lg font-semibold text-slate-900">Basic Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Full Name</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.user_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Email</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.user_email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Date of Birth</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.date_of_birth || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Age</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.age ? `${userDetails.age} years` : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Gender</p>
                        <p className="text-base font-medium text-slate-900 capitalize">{userDetails.gender || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Phone Number</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.phone_number || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin className="w-5 h-5 text-green-600" />
                      <h4 className="text-lg font-semibold text-slate-900">Address Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-600">Address Line 1</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.address_line_1 || 'N/A'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-600">Address Line 2</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.address_line_2 || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">City</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.city || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">State</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.state || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Postal Code</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.postal_code || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Country</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.country || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Medical Information */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Activity className="w-5 h-5 text-red-600" />
                      <h4 className="text-lg font-semibold text-slate-900">Medical Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Blood Group</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.blood_group || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">BMI</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.bmi || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Height</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.height ? `${userDetails.height} cm` : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Weight</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.weight ? `${userDetails.weight} kg` : 'N/A'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-600">Allergies</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.allergies || 'None reported'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-600">Medical Conditions</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.medical_conditions || 'None reported'}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-sm text-slate-600">Current Medications</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.current_medications || 'None reported'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Phone className="w-5 h-5 text-orange-600" />
                      <h4 className="text-lg font-semibold text-slate-900">Emergency Contact</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Contact Name</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.emergency_contact_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Contact Phone</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.emergency_contact_phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Relation</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.emergency_contact_relation || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Profile Status */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertCircle className="w-5 h-5 text-purple-600" />
                      <h4 className="text-lg font-semibold text-slate-900">Profile Status</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Profile Complete</p>
                        <p className={`text-base font-medium ${userDetails.is_profile_complete ? 'text-green-600' : 'text-orange-600'}`}>
                          {userDetails.is_profile_complete ? 'Yes' : 'No'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Last Updated</p>
                        <p className="text-base font-medium text-slate-900">{userDetails.updated_at || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600">No details available for this user</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200">
              <button
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-medical-500 to-medical-600">
              <h3 className="text-lg font-semibold text-white">Create New User</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showCreatePassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCreatePassword(!showCreatePassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-600 hover:text-slate-800 transition-colors"
                  >
                    {showCreatePassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="text-xs text-slate-600">
                * Required fields
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                disabled={creatingUser}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={creatingUser}
                className="px-4 py-2 text-sm font-medium text-white bg-medical-500 hover:bg-medical-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
